/* Multizone BTU Guard — for GWIN (capacity-first, YMQ)
   - Variant picker: radio inputs named like "BTU/Zones-*"
   - YMQ fields:
       .capacity-select.zone-N  (wrapper div) → contains a <select> for zone N capacity
   - "How many zones?" question: .zone-number-question input[type="radio"]
   - Enforces per-variant max BTU, auto-corrects invalid zone picks,
     disables options above budget, and blocks all add-to-cart paths when invalid.
   - Handler types are controlled purely by YMQ via classes like:
       .handler-type.zone-1.capacity-7k  (we do NOT touch those)
*/

// console.log("[Multizone] multizone.js (capacity-first) loaded");

//////////////////// CONFIG ////////////////////

// Max total BTU allowed per variant
const VARIANT_MAX = {
  "18k-2z": 24000,
  "24k-3z": 30000,
  "36k-4z": 42000,
  "42k-5z": 48000,
};

// Per-variant allowed PER-ZONE capacities.
// 7k is allowed for all, handler restrictions are handled by YMQ.
const VARIANT_ALLOWED_CAPS = {
  "18k-2z": [7000, 9000, 12000, 18000],
  "24k-3z": [7000, 9000, 12000, 18000],
  "36k-4z": [7000, 9000, 12000, 18000, 24000],
  "42k-5z": [7000, 9000, 12000, 18000, 24000],
};

// Multizone product handles to run guard on
const MULTIZONE_HANDLES = [
  "gwin-condensers",
  "testing-gwin-multi-zone-condensers-copy",
  // add other multizone handles here
];

//////////////////// HELPERS ////////////////////

function isMultizonePage() {
  const path = window.location.pathname; // e.g. "/products/gwin-condensers"
  return MULTIZONE_HANDLES.some((handle) =>
    path.indexOf(`/products/${handle}`) !== -1
  );
}

const isVisible = (el) => !!(el && el.offsetParent !== null);

// Find the VISIBLE capacity <select> for a zone: .capacity-select.zone-1 etc.
function getCapacitySel(zone) {
  const wrappers = document.querySelectorAll(`.capacity-select.zone-${zone}`);
  for (const wrap of wrappers) {
    const sel = wrap.tagName === "SELECT" ? wrap : wrap.querySelector("select");
    if (sel && isVisible(sel)) return sel;
  }
  return null;
}

// Prefer the inner YMQ spans when present
function optLabel(o) {
  if (!o) return "";

  // YMQ capacity values: <span class="ymq-option-value">12K BTU</span>
  const capSpan = o.querySelector?.(".ymq-option-value");
  if (capSpan) return capSpan.textContent.trim();

  return (o.label || o.textContent || o.value || "").trim();
}

// Selected option label for a <select>
const textOf = (sel) =>
  (sel?.options?.[sel.selectedIndex]
    ? optLabel(sel.options[sel.selectedIndex])
    : sel?.value || ""
  ).trim();

// Parse "7K BTU", "9k BTU/115V", etc. → 7000, 9000, ...
function parseBTU(str) {
  if (!str) return 0;

  str = str.toLowerCase().replace(/\s+/g, ""); // "7kbtu", "9kbtu/115v", etc.

  const match = str.match(/(\d+)(k)?/);
  if (!match) return 0;

  const num = parseInt(match[1], 10);
  return match[2] ? num * 1000 : num;
}

// ---- Variant helpers (radio-based) ----

function getVariantTitle() {
  const radio = document.querySelector(
    'input[type="radio"][name^="BTU/Zones"]:checked'
  );
  if (radio?.value) return radio.value.trim();

  const sw = document.querySelector('[data-selected-swatch-value="BTU/Zones"]');
  if (sw?.textContent) return sw.textContent.trim();

  // fallback: first configured variant label
  return "18k BTU/2 Zones";
}

// Normalize "18k BTU/2 Zones" → "18k-2z"
function normalizeVariantKey(label) {
  if (!label) return "";
  const m = String(label)
    .toLowerCase()
    .match(/(\d+)\s*k?\s*btu.*?\/\s*(\d)\s*zones?/i);
  if (!m) return "";
  const btu = m[1]; // "18"
  const zones = m[2]; // "2"
  return `${btu}k-${zones}z`;
}

function getVariantKey() {
  const key = normalizeVariantKey(getVariantTitle());
  if (VARIANT_MAX[key]) return key;
  return Object.keys(VARIANT_MAX)[0]; // fallback
}

function maxForVariant() {
  const key = getVariantKey();
  return VARIANT_MAX[key] || 24000;
}

function getVariantZoneCount() {
  const key = getVariantKey();
  const m = key.match(/-(\d)z$/);
  return m ? parseInt(m[1], 10) : 1;
}

// Read YMQ "How many zones?" radios, clamp to variant max
function getChosenZoneCount() {
  const checked = document.querySelector(
    '.zone-number-question input[type="radio"]:checked'
  );
  const variantMaxZones = getVariantZoneCount();
  const chosen = checked && checked.value ? parseInt(checked.value, 10) : 1;
  return Math.max(1, Math.min(chosen, variantMaxZones));
}

function allowedCapacitiesForVariant() {
  const key = getVariantKey();
  return VARIANT_ALLOWED_CAPS[key] || [];
}

function eachActiveZone(fn) {
  const need = getChosenZoneCount();
  for (let i = 1; i <= need; i++) fn(i);
}

function getZoneBTU(z) {
  const capSel = getCapacitySel(z);
  if (!capSel) return 0;
  return parseBTU(textOf(capSel));
}

function collectActiveZoneBTUs() {
  const need = getChosenZoneCount();
  const arr = [];
  for (let i = 1; i <= need; i++) arr.push(getZoneBTU(i));
  return arr;
}

// YMQ helpers

const getPlaceholderOption = (sel) =>
  Array.from(sel.options).find(
    (o) =>
      !o.value || o.classList.contains("ymq-option-select-placeholder")
  );

function clearYmqErrors() {
  document.querySelectorAll(".ymq-option-error").forEach((el) => {
    el.textContent = "";
    el.style.display = "none";
  });

  document
    .querySelectorAll(
      ".ymq-options-box, .ymq-option-box-error, .ymq-dropdown"
    )
    .forEach((el) => {
      el.classList.remove(
        "ymq-option-box-error",
        "ymq-error",
        "ymq-field-error",
        "ymq-option-error-border"
      );
      el.style.borderColor = "";
      el.style.boxShadow = "";
      el.style.outline = "";
    });

  document
    .querySelectorAll('.ymq-dropdown[aria-invalid="true"]')
    .forEach((el) => el.setAttribute("aria-invalid", "false"));
}

function clearAllYmqErrors() {
  document.querySelectorAll(".ymq-options-box").forEach((box) => {
    box.classList.remove("ymq-option-box-error");
    const err = box.querySelector(".ymq-option-error");
    if (err) err.textContent = "";
  });
}

// Full reset of ALL YMQ selections (capacity + handler-types)
function fullResetAllZones() {
  const allSelects = document.querySelectorAll(
    '.capacity-select select, .handler-type select'
  );

  allSelects.forEach((sel) => {
    const placeholder = getPlaceholderOption(sel);
    if (placeholder) {
      sel.selectedIndex = placeholder.index;
    } else {
      sel.selectedIndex = 0;
    }
    sel.dispatchEvent(new Event("change", { bubbles: true }));
  });

  clearAllYmqErrors();
}

//////////////////// VARIANT CHANGE RESET ////////////////////

let __mzLastVariantKey = null;

// Reset ONLY capacities (we let YMQ handle which handler-type dropdowns appear)
//////////////////// VARIANT / ZONE CHANGE RESET ////////////////////
// Collect all the selects we care about for a given zone:
// - capacity
// - line set
// - handler type
function getZoneSelects(z) {
  const sels = [];
  document
    .querySelectorAll(
      `.capacity-select.zone-${z} select, 
       .line-set-select.zone-${z} select, 
       .handler-type.zone-${z} select`
    )
    .forEach((sel) => {
      if (sel && isVisible(sel)) sels.push(sel);
    });
  return sels;
}

function resetAllZoneFields() {
  // If you only ever have 2 zones, you can use 2 here; 5 is safe.
  const maxZones = 5;

  for (let z = 1; z <= maxZones; z++) {
    const selects = getZoneSelects(z);
    if (!selects.length) continue;

    selects.forEach((sel) => {
      // show & enable all options
      Array.from(sel.options).forEach((o) => {
        o.hidden = false;
        o.disabled = false;
      });

      // snap back to placeholder if present
      const placeholder = getPlaceholderOption(sel);
      sel.selectedIndex = placeholder ? placeholder.index : 0;

      // clear any YMQ error styling on this box
      const box = sel.closest(".ymq-options-box");
      if (box) {
        box.classList.remove("ymq-option-box-error");
        const err = box.querySelector(".ymq-option-error");
        if (err) err.textContent = "";
      }

      // let YMQ/theme know selection changed
      sel.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  // wipe all YMQ error UI globally
  clearAllYmqErrors();

  // force the guard to reconsider everything next time
  window.__mzInvalid = true;
}

function maybeHandleVariantChange() {
  const key = getVariantKey();
  if (key && key !== __mzLastVariantKey) {
    __mzLastVariantKey = key;
    resetAllZoneFields();
    clearYmqErrors();
  }
}

//////////////////// CORE FILTERING ////////////////////

// Filter capacities in a zone by per-variant allowed capacities
function filterCapacitiesForVariant(z) {
  const capSel = getCapacitySel(z);
  if (!capSel) return;

  const variantAllowed = allowedCapacitiesForVariant();
  const allowedSet = new Set(variantAllowed.map(Number));

  let firstVisible = null;

  Array.from(capSel.options).forEach((o) => {
    const isPlaceholder =
      !o.value || o.classList.contains("ymq-option-select-placeholder");
    if (isPlaceholder) {
      o.hidden = false;
      return;
    }
    const btu = parseBTU(optLabel(o));
    const visible = btu && (allowedSet.size ? allowedSet.has(btu) : true);
    o.hidden = !visible;
    if (visible && !firstVisible) firstVisible = o;
  });

  const cur = capSel.options[capSel.selectedIndex];
  if (cur && cur.hidden && firstVisible) {
    firstVisible.selected = true;
    capSel.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

function pickBestAllowed(sel, budget) {
  const opts = Array.from(sel.options).filter((o) => o.value && !o.hidden);
  const candidates = opts
    .map((o) => ({ o, b: parseBTU(optLabel(o)) }))
    .filter((x) => x.b > 0 && x.b <= budget && !x.o.disabled)
    .sort((a, b) => b.b - a.b);
  if (candidates.length) return candidates[0].o;
  const placeholder = getPlaceholderOption(sel);
  if (placeholder) return placeholder;
  return Array.from(sel.options).find((o) => !o.hidden) || sel.options[0];
}

// Compute, enforce budgets, update UI + __mzInvalid
function enforceTotals() {
  const max = maxForVariant();
  const need = getChosenZoneCount();

  let used = 0;
  const zoneBTUs = {};
  for (let z = 1; z <= need; z++) {
    const b = getZoneBTU(z);
    zoneBTUs[z] = b;
    used += b;
  }

  // Per-zone budgets + auto-correct if current exceeds remaining
  for (let z = 1; z <= need; z++) {
    const capSel = getCapacitySel(z);
    if (!capSel) continue;

    const current = zoneBTUs[z];
    const otherUsed = Math.max(0, used - current);
    const budget = Math.max(0, max - otherUsed);

    Array.from(capSel.options).forEach((o) => {
      const isPlaceholder =
        !o.value || o.classList.contains("ymq-option-select-placeholder");
      if (isPlaceholder) {
        o.disabled = false;
        return;
      }
      const btu = parseBTU(optLabel(o));
      o.disabled = btu > budget;
    });

    const curBTU = parseBTU(textOf(capSel));
    if (curBTU > budget) {
      const next = pickBestAllowed(capSel, budget);
      if (next && !next.selected) {
        next.selected = true;
        capSel.dispatchEvent(new Event("change", { bubbles: true }));
      }
      const newBTU = parseBTU(textOf(capSel));
      used = used - curBTU + newBTU;
      zoneBTUs[z] = newBTU;
    }
  }

  // Valid iff each chosen zone has BTU > 0 and total ≤ max
  let allZonesChosen = true;
  for (let z = 1; z <= need; z++) {
    if (getZoneBTU(z) <= 0) {
      allZonesChosen = false;
      break;
    }
  }
  window.__mzInvalid = !(allZonesChosen && used <= max);

  // Helper text + ATC toggle
  const form = document.querySelector('form[action*="/cart/add"]');
  let msg = document.getElementById("btu-helper");
  if (!msg) {
    msg = document.createElement("div");
    msg.id = "btu-helper";
    msg.style.marginTop = "8px";
    msg.style.fontSize = "14px";
    form?.appendChild(msg);
  }

  // Single-zone path disabled in YMQ, so we always show max helper for multi-zone
  msg.textContent = window.__mzInvalid
    ? `Max total is ${Math.round(max / 1000)}k BTU.`
    : `Max total is ${Math.round(max / 1000)}k BTU.`;
  msg.style.color = window.__mzInvalid ? "#555" : "#555";

  const atc =
    form?.querySelector(
      '[type="submit"], button[name="add"], .product-form__submit'
    );
  if (atc) atc.disabled = !!window.__mzInvalid;

  // console.log(
  //   "[Multizone] used=",
  //   used,
  //   "max=",
  //   max,
  //   "zones=",
  //   collectActiveZoneBTUs(),
  //   "need=",
  //   need,
  //   "variantTitle=",
  //   getVariantTitle(),
  //   "variantKey=",
  //   getVariantKey()
  // );
}

function recalcAll() {
  if (!isMultizonePage()) {
    window.__mzInvalid = false;
    return;
  }
  maybeHandleVariantChange();
  eachActiveZone((z) => filterCapacitiesForVariant(z));
  enforceTotals();
}

//////////////////// ADD-TO-CART GUARDS ////////////////////

function focusFirstInvalid() {
  const need = getChosenZoneCount();
  for (let z = 1; z <= need; z++) {
    const sel = getCapacitySel(z);
    if (!sel) continue;
    const opt = sel.options[sel.selectedIndex];
    const isPlaceholder =
      !sel.value || opt?.classList?.contains("ymq-option-select-placeholder");
    const btu = parseBTU(textOf(sel));
    if (isPlaceholder || !btu || opt?.disabled) {
      sel.focus();
      break;
    }
  }
}

document.addEventListener(
  "submit",
  (e) => {
    const form = e.target;
    if (!form || !/\/cart\/add/.test(form.action || "")) return;
    try {
      recalcAll();
    } catch (_) {}
    if (window.__mzInvalid) {
      e.preventDefault();
      e.stopPropagation();
      alert(
        "Please choose valid capacities. Your total BTU must not exceed the condenser limit."
      );
      focusFirstInvalid();
    }
  },
  true
);

document.addEventListener(
  "click",
  (e) => {
    const btn = e.target.closest(
      "button, [role='button'], a, input[type='submit']"
    );
    if (!btn) return;
    const looksLikeATC = btn.matches(
      '[name="add"], [data-add-to-cart], [data-action="add-to-cart"], .product-form__submit, [id*="ProductSubmitButton"]'
    );
    if (!looksLikeATC) return;
    try {
      recalcAll();
    } catch (_) {}
    if (window.__mzInvalid) {
      e.preventDefault();
      e.stopPropagation();
      alert(
        "Please choose valid capacities. Your total BTU must not exceed the condenser limit."
      );
      focusFirstInvalid();
    }
  },
  true
);

// Network-level guard
(function patchCartNetworkGuards() {
  const isCartAdd = (url) => {
    try {
      const u = typeof url === "string" ? url : url?.url || "";
      return /\/cart\/add(\.js)?/i.test(u);
    } catch {
      return false;
    }
  };

  const _fetch = window.fetch;
  window.fetch = function (url, opts) {
    if (isCartAdd(url)) {
      try {
        recalcAll();
      } catch (_) {}
      if (window.__mzInvalid) {
        alert(
          "Please choose valid capacities. Your total BTU must not exceed the condenser limit."
        );
        focusFirstInvalid();
        return Promise.reject(
          new Error("Blocked add-to-cart: invalid multizone selection")
        );
      }
    }
    return _fetch.apply(this, arguments);
  };

  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;
  let _mz_isCartAdd = false;

  XMLHttpRequest.prototype.open = function (method, url) {
    _mz_isCartAdd = isCartAdd(url);
    return _open.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (_mz_isCartAdd) {
      try {
        recalcAll();
      } catch (_) {}
      if (window.__mzInvalid) {
        alert(
          "Please choose valid capacities. Your total BTU must not exceed the condenser limit."
        );
        focusFirstInvalid();
        throw new Error(
          "Blocked XHR add-to-cart: invalid multizone selection"
        );
      }
    }
    return _send.apply(this, arguments);
  };
})();

//////////////////// VARIANT PICKER POSITION ////////////////////

function moveVariantPickerAboveYMQ() {
  const container = document.querySelector(".product__block--variant_picker");
  if (!container) return false;

  const ymq = container.querySelector("ymq-option");
  const variant = container.querySelector("variant-selects");
  if (!ymq || !variant) return false;

  if (variant.dataset.mzMoved === "1") return true;

  const relation = ymq.compareDocumentPosition(variant);
  const ymqBeforeVariant =
    relation & Node.DOCUMENT_POSITION_FOLLOWING;

  if (ymqBeforeVariant) {
    container.insertBefore(variant, ymq);
    variant.dataset.mzMoved = "1";
    // console.log("[Multizone] Moved variant picker above <ymq-option>");
  }

  return true;
}

//////////////////// EVENTS ////////////////////

document.addEventListener("change", (e) => {
  if (
    e.target.matches(
      'input[type="radio"][name^="BTU/Zones"], .zone-number-question input[type="radio"], .capacity-select select, .ymq-dropdown'
    )
  ) {
    try {
      // If the user changed "How many zones?" or BTU/Zones, nuke all selections
      if (
        e.target.matches(
          '.zone-number-question input[type="radio"], input[type="radio"][name^="BTU/Zones"]'
        )
      ) {
        resetAllZoneFields();
      }

      recalcAll();

      // If the user changed "How many zones?", clear YMQ error UI again
      if (e.target.matches('.zone-number-question input[type="radio"]')) {
        setTimeout(() => {
          clearAllYmqErrors();
        }, 0);
      }
    } catch (err) {
      console.error("[Multizone] recalc error:", err);
    }
  }
});


// Theme variant events – when BTU/Zones variant changes
document.addEventListener(
  "variant:changed",
  () => {
    try {
      resetAllZoneFields();
      clearYmqErrors();
      recalcAll();
    } catch (err) {
      console.error("[Multizone] variant:changed handler error:", err);
    }
  },
  true
);

document.addEventListener(
  "product:variant-change",
  () => {
    try {
      resetAllZoneFields();
      clearYmqErrors();
      recalcAll();
    } catch (err) {
      console.error("[Multizone] product:variant-change handler error:", err);
    }
  },
  true
);

// Initial pass: move variant picker & run first calc
window.addEventListener("load", () => {
  try {
    let tries = 0;
    const maxTries = 40; // ~8s at 200ms interval

    const intervalId = setInterval(() => {
      tries++;

      const moved = moveVariantPickerAboveYMQ();

      if (moved || tries >= maxTries) {
        clearInterval(intervalId);
      }
    }, 200);

    recalcAll();
  } catch (e) {
    console.error("[Multizone] init error:", e);
  }
});

// Debug probe
window.mzProbe = function () {
  console.log(
    "[Multizone] variantTitle=",
    getVariantTitle(),
    "variantKey=",
    getVariantKey(),
    "max=",
    maxForVariant()
  );
  const need = getChosenZoneCount();
  for (let z = 1; z <= need; z++) {
    console.log(`[Z${z}] capacity:`, textOf(getCapacitySel(z)), "=>", getZoneBTU(z));
  }
};
